<template>
    <vx-data-table
        :columns="dataTableColumns"
        :rows="groups"
        rowKey="group_id"
        :loading="grouped ? sectionsLoading : loading"
        :totalCount="grouped ? null : totalCount"
        :hasMore="hasMore"
        :loadingMore="loadingMore"
        :sections="sections"
        toolbar
        :groupByOptions="kioskTeamGroupGroupByOptions"
        searchPlaceholder="Group name, external ID, phone number or owner email"
        :columnsDescription="columnsDescription"
        :filterFields="filterFields"
        :search="search"
        :filters="filters"
        v-model:sort="sort"
        v-model:density="density"
        :visibleColumns="visibleColumnsWithinLimit"
        v-model:groupBy="groupBy"
        v-model:collapsedSections="collapsedSections"
        @update:search="updateSearch"
        @update:filters="updateFilters"
        @update:visibleColumns="updateVisibleColumns"
        @loadMore="loadMore"
    >
        <template v-slot:toolbar-actions>
            <vx-dropdown placement="right">
                <vx-button data-test="more-actions" type="button" color="white" shadow aria-label="More actions">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>
                <template v-slot:items>
                    <vx-dropdown-item data-test="export-groups" @click="exportModalVisible = true">
                        Export
                        <font-awesome-icon widthAuto :icon="faFileExport" />
                    </vx-dropdown-item>
                    <router-dropdown-item data-test="import-groups" :to="groupImportHref(props.teamId)">
                        Import
                        <font-awesome-icon widthAuto :icon="faFileArrowUp" />
                    </router-dropdown-item>
                </template>
            </vx-dropdown>
            <vx-button data-test="new-group" color="primary" :href="newGroupHref(props.teamId)">
                New Group
                <font-awesome-icon widthAuto :icon="faCirclePlus" />
            </vx-button>
        </template>

        <template v-slot:cell-name="{ row }">
            <span class="flex min-w-0 items-center gap-2">
                <vx-popover
                    v-if="!externalIdVisible"
                    hover
                    teleport
                    :arrow="true"
                    placement="top"
                    class="shrink-0 cursor-pointer"
                    @click="copyText(row.group_id)"
                >
                    <vx-badge data-test="group-id-badge" color="slate" size="xs">
                        <copy-button label="external ID" :value="row.group_id">
                            {{ shortGroupId(row.group_id) }}
                        </copy-button>
                    </vx-badge>
                    <template v-slot:content>
                        <div class="text-center">
                            {{ row.group_id }}
                            <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                        </div>
                    </template>
                </vx-popover>
                <a
                    data-test="group-link"
                    :href="groupHref(props.teamId, row)"
                    class="flex min-w-0 font-bold text-slate-800"
                >
                    <vx-badge v-if="row.setup_required" data-test="setup-required" color="rose" size="xs">
                        Setup Required <font-awesome-icon widthAuto :icon="faAsterisk" class="text-rose-500" />
                    </vx-badge>
                    <vx-data-table-truncated-text v-else :text="row.name" />
                </a>
            </span>
        </template>

        <template v-slot:cell-status="{ row }">
            <group-status-badge :status="row.status" />
        </template>

        <template v-slot:cell-owner="{ row }">
            <kiosk-team-group-owner-cell
                :owner="row.owner"
                :ownerEmail="row.owner_email"
                :ownerInvite="row.owner_invite"
            />
        </template>

        <template v-slot:cell-signup="{ row }">
            <kiosk-team-group-signup-cell
                :teamId="props.teamId"
                :groupId="row.group_id"
                :signup="row.signup"
                :owner="row.owner"
                :ownerEmail="row.owner_email"
                :ownerInviteId="row.owner_invite_id"
                :ownerLastLoginAt="row.owner_last_login_at"
                :ownerPredatesLoginRecords="row.owner_predates_login_records"
                @resent="refreshAfterInviteResent"
            />
        </template>

        <template v-slot:cell-phone_number="{ row }">
            <kiosk-team-group-number-cell
                :phoneNumber="row.phone_number"
                :phoneNumbersCount="row.phone_numbers_count"
                :numberState="row.number_state"
            />
        </template>

        <template v-slot:cell-terms="{ row }">
            <kiosk-team-group-terms-badge :terms="row.terms" :termsDue="row.terms_due" />
        </template>

        <template v-slot:cell-billing="{ row }">
            <kiosk-team-group-billing-badge
                :billing="row.billing"
                :hasRates="row.has_rates"
                :hasBillingAddress="row.has_billing_address"
                :paymentMethod="row.payment_method"
                :paymentTermsDays="row.payment_terms_days"
                :orgUnitName="row.org_unit_name"
                :orgUnitHref="row.org_unit_id === null ? '' : orgUnitHref(props.teamId, row.org_unit_id)"
            />
        </template>

        <template v-slot:cell-ten_dlc_campaign_status="{ row }">
            <kiosk-team-group-ten-dlc-campaign-cell
                :status="row.ten_dlc_campaign_status"
                :campaignId="row.ten_dlc_campaign_id"
                :label="row.ten_dlc_campaign_label"
                :brandVerified="row.ten_dlc_campaign_brand_verified"
                :vettingScore="row.ten_dlc_campaign_brand_vetting_score"
                :useCase="row.ten_dlc_campaign_use_case"
                :attClass="row.ten_dlc_campaign_att_class"
                :attachOrderStatus="row.ten_dlc_attach_order_status"
                :attachOrderError="row.ten_dlc_attach_order_error"
                :attachOrderErrorCode="row.ten_dlc_attach_order_error_code"
                :attachOrderPlacedAt="row.ten_dlc_attach_order_placed_at"
            />
        </template>

        <template v-slot:cell="{ row, column }">
            <vx-data-table-truncated-text
                v-if="plainCellText(row, column.key) !== ''"
                :text="plainCellText(row, column.key)"
            />
            <span v-else class="text-slate-500">-</span>
        </template>

        <template v-slot:cell-users_count="{ row }">
            {{ formatNumber(row.users_count) }}
        </template>

        <template v-slot:cell-org_unit_id="{ row }">
            <vx-data-table-truncated-text v-if="row.org_unit_id !== null" :text="row.org_unit_name">
                <a data-test="org-unit-link" :href="orgUnitHref(props.teamId, row.org_unit_id)">
                    {{ row.org_unit_name }}
                </a>
            </vx-data-table-truncated-text>
        </template>

        <template v-slot:header-collections_count="{ column }">
            <vx-popover hover teleport :arrow="true" placement="top">
                <span>{{ column.label }}</span>
                <template v-slot:content>
                    <div class="max-w-[240px] text-center">
                        Corporate collections the group belongs to. OU collections are not counted.
                    </div>
                </template>
            </vx-popover>
        </template>

        <template v-slot:cell-created_at="{ row }">
            <vx-popover v-if="row.created_at" hover teleport :arrow="true" placement="top">
                <span>{{ shortDate(row.created_at) }}</span>
                <template v-slot:content>{{ shortDateTime(row.created_at) }}</template>
            </vx-popover>
            <span v-else class="text-slate-500">-</span>
        </template>

        <template v-slot:section-header-status="{ section }">
            <group-status-badge v-if="isGroupStatus(section.value)" :status="section.value" />
        </template>

        <template v-slot:section-count="{ section }">
            {{ pluralized('group', section.total, true) }}
        </template>

        <template v-slot:section-header-org_unit_id="{ section }">
            <span v-if="sectionNames[section.key]">{{ sectionNames[section.key] }}</span>
            <span v-else class="text-slate-500">No OU</span>
        </template>

        <template v-slot:section-header-terms="{ section }">
            <kiosk-team-group-terms-badge v-if="isTerms(section.value)" :terms="section.value" />
        </template>

        <template v-slot:actions="{ row }">
            <vx-button
                v-if="row.setup_required"
                data-test="setup-group"
                :href="groupHref(props.teamId, row)"
                color="danger"
                size="xs"
            >
                Setup
                <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
            <kiosk-team-group-row-actions
                v-else
                :teamId="props.teamId"
                :group="row"
                :groupHref="groupHref(props.teamId, row)"
                @statusUpdated="updateGroupStatus(row.group_id, $event)"
                @deleted="removeGroup(row.group_id)"
            />
        </template>

        <template v-slot:empty>
            <div v-if="narrowed" class="flex flex-col items-center gap-6 text-xl text-slate-500">
                Nothing matches these filters
                <vx-button data-test="clear-filters" color="muted-dark" @click="clearFilters"
                    >Clear filters

                    <font-awesome-icon widthAuto :icon="faCircleX" />
                </vx-button>
            </div>
            <div v-else class="flex flex-col items-center gap-6 text-xl text-slate-500">
                No Groups yet
                <vx-button data-test="empty-new-group" :href="newGroupHref(props.teamId)">
                    New Group
                    <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </div>
        </template>
    </vx-data-table>

    <kiosk-team-groups-export-modal
        v-model:visible="exportModalVisible"
        :teamId="props.teamId"
        :query="query"
        :sort="sort"
        :filters="filters"
        :tableColumns="columns"
        :visibleColumns="visibleColumnsWithinLimit"
        :matchingCount="grouped ? null : totalCount"
    />
</template>

<script lang="ts" setup>
import {
    faAsterisk,
    faCirclePlus,
    faCircleX,
    faEllipsis,
    faFileArrowUp,
    faFileExport,
    faFilePen,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDataTable,
    VxDataTableTruncatedText,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    type VxDataTableColumn,
    type VxDataTableDensity,
    type VxDataTableFilterSelectable,
    type VxDataTableLoadMore,
} from '@voxie/frontend-components';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import { isCancel } from 'axios';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { pluralized } from '~/components/filters';
import CopyButton from '~/components/general/CopyButton.vue';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';
import RouterDropdownItem from '~/components/general/RouterDropdownItem.vue';
import { useCopyText } from '~/composables/useCopyText';
import { useSearchRequest } from '~/composables/useSearchRequest';
import { useToasted } from '~/composables/useToasted';
import { adminOrgUnitsClient, adminTeamGroupsClient } from '~/services';
import type { ExtendedAxiosRequestConfig } from '~/services/BaseClient';
import type { GroupStatus } from '~/constants/groupStatuses';
import type {
    KioskTeamGroup,
    KioskTeamGroupGroupBy,
    KioskTeamGroupSectionsParams,
} from '~/services/adminTeamGroupsClient.types';
import { shortDate, shortDateTime } from '~/utils/date';
import debounce from '~/utils/debounce';
import { formatNumber, isEqual } from '~/utils/helpers';
import KioskTeamGroupRowActions from './KioskTeamGroupRowActions.vue';
import KioskTeamGroupsExportModal from './KioskTeamGroupsExportModal.vue';
import KioskTeamGroupBillingBadge from './KioskTeamGroupBillingBadge.vue';
import KioskTeamGroupNumberCell from './KioskTeamGroupNumberCell.vue';
import KioskTeamGroupOwnerCell from './KioskTeamGroupOwnerCell.vue';
import KioskTeamGroupSignupCell from './KioskTeamGroupSignupCell.vue';
import KioskTeamGroupTenDlcCampaignCell from './KioskTeamGroupTenDlcCampaignCell.vue';
import KioskTeamGroupTermsBadge from './KioskTeamGroupTermsBadge.vue';
import {
    KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_COLUMNS,
    KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_FILTERS,
    KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_VALUE_LENGTH,
    KIOSK_TEAM_GROUPS_MAX_FILTER_VALUES,
    awaitsRows,
    columnsStorageKey,
    customAttributeColumns,
    customAttributeKeysOf,
    defaultSort,
    densityStorageKey,
    exceedsCustomAttributeColumnLimit,
    exceedsCustomAttributeFilterLimit,
    exceedsCustomAttributeValueLength,
    exceedsFilterValueLimit,
    expandedSectionKeys,
    filtersAllowStatus,
    groupHref,
    groupImportHref,
    isColumnVisible,
    isGroupStatus,
    isNarrowed,
    isTerms,
    kioskTeamGroupColumns,
    kioskTeamGroupFilterFields,
    kioskTeamGroupGroupByOptions,
    needsReload,
    newGroupHref,
    nextCursorFrom,
    orgUnitHref,
    noOrgUnitOption,
    plainCellText,
    sectionCursorsFrom,
    sectionNamesFrom,
    sectionOfGroup,
    shortGroupId,
    toDataTableSection,
    toGroupSectionsParams,
    toGroupsCountParams,
    toGroupsParams,
    toSearchQuery,
    toSectionPageParams,
    unloadedSectionKeys,
    withGroupStatus,
    withSectionGroupStatus,
    withTrimmedCustomAttributeValues,
    withinCustomAttributeColumnLimit,
    withoutSectionGroup,
    withoutUnlistedCustomAttributeColumns,
    type KioskTeamGroupColumn,
    type KioskTeamGroupsFilter,
    type KioskTeamGroupsSection,
    type KioskTeamGroupsSort,
} from './kioskTeamGroupsTableHelpers';

interface OrgUnitOption {
    value: string;
    label: string;
}

interface AdminOrgUnit {
    id: number;
    name: string;
}

interface OrgUnitLookupParams {
    query?: string;
    ids?: string[];
}

const props = defineProps<{
    teamId: number;
}>();

const toasted = useToasted();
const { copyText } = useCopyText();

const groupsCancelKey = Symbol('kiosk-team-groups');
const countCancelKey = Symbol('kiosk-team-groups-count');
const sectionsCancelKey = Symbol('kiosk-team-group-sections');
const sectionPageCancelKeys = new Map<string, symbol>();

const groups = ref<KioskTeamGroup[]>([]);
const nextCursor = ref<string | null>(null);
const totalCount = ref<number | null>(null);
const loading = ref(true);
const sectionsLoading = ref(false);
const loadingMore = ref(false);

const search = ref('');
const query = ref('');
const sort = ref<KioskTeamGroupsSort>(defaultSort);
const filters = ref<KioskTeamGroupsFilter[]>([]);
const groupBy = ref<KioskTeamGroupGroupBy | null>(null);

const sections = ref<KioskTeamGroupsSection[] | null>(null);
const sectionNames = ref<Record<string, string | null>>({});
const sectionCursors = ref<Record<string, string | null>>({});
const collapsedSections = ref<string[]>([]);
const exportModalVisible = ref(false);
const customAttributeKeys = ref<string[]>([]);
const customAttributeKeysTruncated = ref(false);

const visibleColumns = useLocalStorage<string[] | null>(() => columnsStorageKey(props.teamId), null, {
    serializer: StorageSerializers.object,
});
const density = useLocalStorage<VxDataTableDensity>(() => densityStorageKey(props.teamId), 'comfortable', {
    writeDefaults: false,
});

const {
    loading: orgUnitsLoading,
    response: orgUnitSearchResults,
    createSearch: createOrgUnitSearch,
    cancelSearch: cancelOrgUnitSearch,
    cancelKey: orgUnitsCancelKey,
} = useSearchRequest<OrgUnitOption[]>(adminOrgUnitsClient, 'kiosk-team-group-org-units');

const knownOrgUnits = ref(new Map<string, OrgUnitOption>());

const selectedOrgUnitIds = computed(() =>
    (filters.value.find((filter) => filter.key === 'org_unit_id')?.values ?? []).filter(
        (value) => value !== noOrgUnitOption.value
    )
);

const toOrgUnitOption = (orgUnit: AdminOrgUnit): OrgUnitOption => ({ value: String(orgUnit.id), label: orgUnit.name });

const fetchOrgUnits = async (params: OrgUnitLookupParams, config?: ExtendedAxiosRequestConfig) => {
    const { data } = await adminOrgUnitsClient.listOrgUnits(
        { team_id: props.teamId, sort_field: 'name', sort_dir: 'asc', ...params },
        config
    );

    return (data.data as AdminOrgUnit[]).map(toOrgUnitOption);
};

const rememberOrgUnits = (options: OrgUnitOption[]) => {
    options.forEach((option) => knownOrgUnits.value.set(option.value, option));
};

const searchOrgUnits = createOrgUnitSearch((orgUnitQuery: string) =>
    fetchOrgUnits({ query: orgUnitQuery }, { cancelKey: orgUnitsCancelKey })
);

const resolveSelectedOrgUnits = async (ids: string[]) => {
    const unknownIds = ids.filter((id) => !knownOrgUnits.value.has(id));

    if (unknownIds.length) {
        rememberOrgUnits(await fetchOrgUnits({ ids: unknownIds }).catch(() => []));
    }
};

const orgUnitOptions = computed(() => {
    const selectedIds = new Set(selectedOrgUnitIds.value);
    const selectedOptions = selectedOrgUnitIds.value.flatMap((id) => knownOrgUnits.value.get(id) ?? []);
    const unselectedResults = (orgUnitSearchResults.value ?? []).filter((option) => !selectedIds.has(option.value));

    return [...selectedOptions, ...unselectedResults];
});

const orgUnitSelectable = computed<VxDataTableFilterSelectable>(() => ({
    options: orgUnitOptions.value,
    label: 'label',
    reduce: (option: OrgUnitOption) => option.value,
    loading: orgUnitsLoading.value,
    placeholder: 'Search organizational units',
    onSearch: (orgUnitQuery: string) => searchOrgUnits(orgUnitQuery),
}));

const columns = computed<KioskTeamGroupColumn[]>(() => [
    ...kioskTeamGroupColumns,
    ...customAttributeColumns(customAttributeKeys.value),
]);
const dataTableColumns = computed(() => columns.value as VxDataTableColumn<KioskTeamGroup>[]);
const filterFields = computed(() =>
    kioskTeamGroupFilterFields(orgUnitSelectable.value, customAttributeKeys.value, customAttributeKeysTruncated.value)
);
const visibleColumnsWithinLimit = computed(() => withinCustomAttributeColumnLimit(visibleColumns.value));
const visibleCustomAttributeKeys = computed(() => customAttributeKeysOf(visibleColumnsWithinLimit.value ?? []));
const countParams = computed(() => toGroupsCountParams(query.value, filters.value));
const listParams = computed(() =>
    toGroupsParams(query.value, sort.value, filters.value, visibleCustomAttributeKeys.value)
);
const sectionsParams = computed(() =>
    groupBy.value === null
        ? null
        : toGroupSectionsParams(query.value, sort.value, filters.value, groupBy.value, visibleCustomAttributeKeys.value)
);
const customAttributeColumnLimitMessage = `Show up to ${KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_COLUMNS} custom attribute columns at a time.`;
const columnsDescription = computed(() => {
    const listedKeyCount = customAttributeKeys.value.length;

    if (listedKeyCount === 0) {
        return undefined;
    }

    return customAttributeKeysTruncated.value
        ? `${customAttributeColumnLimitMessage} Only the first ${listedKeyCount} custom attributes are listed.`
        : customAttributeColumnLimitMessage;
});
const grouped = computed(() => groupBy.value !== null);
const narrowed = computed(() => isNarrowed(countParams.value));
const hasMore = computed(() => nextCursor.value !== null);
const externalIdVisible = computed(() => isColumnVisible(visibleColumns.value, 'group_id'));

const loadCustomAttributeKeys = async () => {
    try {
        const { data } = await adminTeamGroupsClient.getGroupCustomAttributeKeys(props.teamId);

        customAttributeKeys.value = data.data;
        customAttributeKeysTruncated.value = data.truncated;
        visibleColumns.value = withoutUnlistedCustomAttributeColumns(visibleColumns.value, data.data);
    } catch {
        customAttributeKeys.value = [];
        toasted.global.platform_error();
    }
};

const loadGroups = async () => {
    loading.value = true;
    loadingMore.value = false;
    nextCursor.value = null;

    try {
        const { data } = await adminTeamGroupsClient.getGroups(props.teamId, listParams.value, {
            cancelKey: groupsCancelKey,
        });

        groups.value = data.data;
        nextCursor.value = nextCursorFrom(data.next_page_url);
        loading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        groups.value = [];
        totalCount.value = null;
        loading.value = false;
        toasted.global.platform_error();
    }
};

const loadMoreGroups = async () => {
    loadingMore.value = true;

    try {
        const { data } = await adminTeamGroupsClient.getGroups(
            props.teamId,
            { ...listParams.value, cursor: nextCursor.value },
            { cancelKey: groupsCancelKey }
        );

        groups.value = [...groups.value, ...data.data];
        nextCursor.value = nextCursorFrom(data.next_page_url);
        loadingMore.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        loadingMore.value = false;
        toasted.global.platform_error();
    }
};

const sectionPageCancelKey = (sectionKey: string): symbol => {
    const cancelKey = sectionPageCancelKeys.get(sectionKey) ?? Symbol(`kiosk-team-group-section-${sectionKey}`);
    sectionPageCancelKeys.set(sectionKey, cancelKey);

    return cancelKey;
};

const cancelSectionPageRequests = () => {
    sectionPageCancelKeys.forEach((cancelKey) => adminTeamGroupsClient.cancelRequest(cancelKey));
    sectionPageCancelKeys.clear();
};

const resetSections = () => {
    cancelSectionPageRequests();
    sections.value = null;
    sectionCursors.value = {};
    sectionNames.value = {};
    collapsedSections.value = [];
};

const loadSections = async (params: KioskTeamGroupSectionsParams, keepCurrentSections = false) => {
    if (keepCurrentSections) {
        cancelSectionPageRequests();
    } else {
        resetSections();
    }

    sectionsLoading.value = true;

    try {
        const { data } = await adminTeamGroupsClient.getGroupSections(props.teamId, params, {
            cancelKey: sectionsCancelKey,
        });

        sectionCursors.value = sectionCursorsFrom(data.data);
        sectionNames.value = sectionNamesFrom(data.data);
        sections.value = data.data.map(toDataTableSection(params.group_by));
        collapsedSections.value = unloadedSectionKeys(data.data);
        sectionsLoading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        sections.value = [];
        sectionsLoading.value = false;
        toasted.global.platform_error();
    }
};

const updateSection = (sectionKey: string, update: (section: KioskTeamGroupsSection) => KioskTeamGroupsSection) => {
    if (sections.value === null) {
        return;
    }

    sections.value = sections.value.map((section) => (section.key === sectionKey ? update(section) : section));
};

const loadSectionPage = async (sectionKey: string) => {
    const params = sectionsParams.value;

    if (params === null) {
        return;
    }

    updateSection(sectionKey, (section) => ({ ...section, loading: true }));

    try {
        const { data } = await adminTeamGroupsClient.getGroups(
            props.teamId,
            toSectionPageParams(params, sectionKey, sectionCursors.value[sectionKey] ?? null),
            { cancelKey: sectionPageCancelKey(sectionKey) }
        );
        const cursor = nextCursorFrom(data.next_page_url);

        sectionCursors.value = { ...sectionCursors.value, [sectionKey]: cursor };
        updateSection(sectionKey, (section) => ({
            ...section,
            rows: [...section.rows, ...data.data],
            hasMore: cursor !== null,
            loading: false,
        }));
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        updateSection(sectionKey, (section) => ({ ...section, loading: false }));
        toasted.global.platform_error();
    }
};

const loadExpandedSections = (collapsed: string[], previouslyCollapsed: string[]) => {
    const expandedKeys = expandedSectionKeys(collapsed, previouslyCollapsed);

    (sections.value ?? [])
        .filter((section) => expandedKeys.includes(section.key) && awaitsRows(section))
        .forEach((section) => loadSectionPage(section.key));
};

const loadMore = ({ key }: VxDataTableLoadMore) => {
    if (key === null) {
        loadMoreGroups();
        return;
    }

    loadSectionPage(key);
};

const loadCount = async () => {
    try {
        const { data } = await adminTeamGroupsClient.getGroupsCount(props.teamId, countParams.value, {
            cancelKey: countCancelKey,
        });

        totalCount.value = data.count;
    } catch (error) {
        if (!isCancel(error)) {
            totalCount.value = null;
        }
    }
};

const reloadGroups = () => {
    if (sectionsParams.value === null) {
        loadGroups();
        return;
    }

    loadSections(sectionsParams.value, true);
};

const reloadCountUnlessGrouped = () => {
    if (!grouped.value) {
        loadCount();
    }
};

const showGroupsList = () => {
    adminTeamGroupsClient.cancelRequest(sectionsCancelKey);
    resetSections();
    loadGroups();
    loadCount();
};

const changeGrouping = () => {
    if (sectionsParams.value === null) {
        showGroupsList();
        return;
    }

    loadSections(sectionsParams.value);
};

const applySearch = debounce((value: string) => {
    query.value = toSearchQuery(value);
});

const updateSearch = (value: string) => {
    search.value = value;
    applySearch(value);
};

const updateFilters = (filtersToApply: KioskTeamGroupsFilter[]) => {
    const next = withTrimmedCustomAttributeValues(filtersToApply);

    if (exceedsFilterValueLimit(next)) {
        toasted.error(`Select up to ${KIOSK_TEAM_GROUPS_MAX_FILTER_VALUES} values per filter.`);
        return;
    }

    if (exceedsCustomAttributeFilterLimit(next)) {
        toasted.error(`Add up to ${KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_FILTERS} custom attribute filters.`);
        return;
    }

    if (exceedsCustomAttributeValueLength(next)) {
        toasted.error(
            `Type up to ${KIOSK_TEAM_GROUPS_MAX_CUSTOM_ATTRIBUTE_VALUE_LENGTH} characters per custom attribute value.`
        );
        return;
    }

    filters.value = next;
};

const updateVisibleColumns = (next: string[] | null) => {
    if (exceedsCustomAttributeColumnLimit(next)) {
        toasted.error(customAttributeColumnLimitMessage);
        return;
    }

    visibleColumns.value = next;
};

const clearFilters = () => {
    applySearch.cancel();
    search.value = '';
    query.value = '';
    filters.value = [];
};

const removeListGroup = (groupId: string) => {
    groups.value = groups.value.filter((group) => group.group_id !== groupId);

    if (!groups.value.length && hasMore.value) {
        loadGroups();
    }
};

const removeSectionGroup = (groupId: string) => {
    if (sections.value === null) {
        return;
    }

    const section = sectionOfGroup(sections.value, groupId);

    if (section === undefined) {
        return;
    }

    sections.value = withoutSectionGroup(sections.value, groupId);

    const emptiedSection = sections.value.find((candidate) => candidate.key === section.key && awaitsRows(candidate));

    if (emptiedSection !== undefined) {
        loadSectionPage(emptiedSection.key);
    }
};

const removeGroup = (groupId: string) => {
    if (grouped.value) {
        removeSectionGroup(groupId);
    } else {
        removeListGroup(groupId);
    }

    reloadCountUnlessGrouped();
};

const refreshAfterInviteResent = () => {
    reloadGroups();
    reloadCountUnlessGrouped();
};

const updateGroupStatus = (groupId: string, status: GroupStatus) => {
    if (!filtersAllowStatus(filters.value, status)) {
        removeGroup(groupId);
        return;
    }

    reloadCountUnlessGrouped();

    if (!grouped.value) {
        groups.value = withGroupStatus(groups.value, groupId, status);
        return;
    }

    if (groupBy.value === 'status') {
        reloadGroups();
        return;
    }

    if (sections.value !== null) {
        sections.value = withSectionGroupStatus(sections.value, groupId, status);
    }
};

watch(groupBy, changeGrouping);
watch(listParams, (next, previous) => {
    if (needsReload(next, previous)) {
        reloadGroups();
    }
});

watch(countParams, (next, previous) => {
    if (!isEqual(next, previous)) {
        reloadCountUnlessGrouped();
    }
});
watch(collapsedSections, loadExpandedSections);

watch(orgUnitSearchResults, (next) => rememberOrgUnits(next ?? []));
watch(selectedOrgUnitIds, resolveSelectedOrgUnits, { immediate: true });

onMounted(() => {
    loadGroups();
    loadCount();
    loadCustomAttributeKeys();
    searchOrgUnits('');
});

onBeforeUnmount(() => {
    applySearch.cancel();
    cancelOrgUnitSearch();
});
</script>
